"use client";

import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import useLockScroll from "@/hooks/useLockScroll";

export interface VideoModalProps {
  open: boolean;
  onClose: () => void;
  videoUrl?: string;
  title?: string;
}

function formatVideoUrl(url: string = ""): string {
  if (!url) return "";
  let cleanUrl = url.trim();
  let videoId = "";

  if (cleanUrl.includes("youtube.com/watch?v=")) {
    videoId = cleanUrl.split("v=")[1]?.split("&")[0] || "";
  } else if (cleanUrl.includes("youtu.be/")) {
    videoId = cleanUrl.split("youtu.be/")[1]?.split("?")[0] || "";
  } else if (cleanUrl.includes("youtube.com/embed/")) {
    videoId = cleanUrl.split("youtube.com/embed/")[1]?.split("?")[0] || "";
  }

  if (videoId) {
    return `https://www.youtube.com/embed/${videoId}?autoplay=1&loop=1&playlist=${videoId}&playsinline=1&enablejsapi=1`;
  }

  return cleanUrl;
}

export default function VideoModal({
  open,
  onClose,
  videoUrl,
  title,
}: VideoModalProps) {
  const [mounted, setMounted] = useState(false);

  useLockScroll(open);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (open) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  if (!open || !mounted || !videoUrl) return null;

  const formattedUrl = formatVideoUrl(videoUrl);
  const isDirectVideo =
    formattedUrl.endsWith(".mp4") ||
    formattedUrl.endsWith(".webm") ||
    formattedUrl.endsWith(".ogg");

  const modalContent = (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[9999] bg-black/50 flex flex-col items-center justify-center backdrop-blur-lg p-4 sm:p-6 md:p-10 select-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        {/* TOP BAR: OPTIONAL TITLE & CLOSE BUTTON */}
        <div className="absolute top-4 sm:top-6 left-4 sm:left-6 right-4 sm:right-6 flex items-center justify-between z-20 pointer-events-none">
          {title ? (
            <div className="text-white text-xs sm:text-sm font-sans font-medium tracking-wide bg-black/50 backdrop-blur-md px-4 py-2 rounded-md border border-white/10 max-w-[calc(100%-80px)] truncate pointer-events-auto">
              {title}
            </div>
          ) : (
            <div />
          )}

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="w-10 h-10 rounded-full bg-black/60 text-white border border-white/20 flex items-center justify-center hover:bg-white hover:text-black transition-colors cursor-pointer pointer-events-auto focus:outline-none shadow-lg"
            aria-label="Close video"
          >
            ✕
          </button>
        </div>

        {/* CENTER VIDEO CONTAINER */}
        <div
          className="relative w-full max-w-5xl aspect-video bg-black rounded-lg overflow-hidden border border-white/15 shadow-2xl z-10 pointer-events-auto my-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {isDirectVideo ? (
            <video
              ref={(el) => {
                if (el) {
                  el.volume = 0.5;
                }
              }}
              src={formattedUrl}
              controls
              autoPlay
              loop
              playsInline
              className="w-full h-full object-contain"
            />
          ) : (
            <iframe
              src={formattedUrl}
              title={title || "Video Player"}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full border-none"
            />
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
}
